import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../utils/api';

export interface RuntimeSettings {
  autoRefreshIntervalSeconds: number;
}

const DEFAULT_AUTO_REFRESH_INTERVAL_SECONDS = 30;

function normalizeAutoRefreshInterval(value: unknown): number {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return DEFAULT_AUTO_REFRESH_INTERVAL_SECONDS;
  }

  if (parsed === 0) {
    return 0;
  }

  return Math.min(3600, Math.max(5, Math.round(parsed)));
}

export function useRuntimeSettings() {
  const [settings, setSettings] = useState<RuntimeSettings>({
    autoRefreshIntervalSeconds: DEFAULT_AUTO_REFRESH_INTERVAL_SECONDS,
  });
  const [loading, setLoading] = useState(true);

  const reloadRuntimeSettings = useCallback(async () => {
    try {
      const response = await apiClient('/api/settings/runtime');

      if (!response.ok) {
        return;
      }

      const payload = await response.json();
      const data = payload?.data ?? payload;

      setSettings({
        autoRefreshIntervalSeconds: normalizeAutoRefreshInterval(
          data?.autoRefreshIntervalSeconds,
        ),
      });
    } catch (error) {
      // Keep the safe default when the runtime-settings endpoint is temporarily unavailable.
      console.warn('Unable to load runtime settings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reloadRuntimeSettings();
  }, [reloadRuntimeSettings]);

  return {
    ...settings,
    loading,
    reloadRuntimeSettings,
  };
}
